import React, { type MutableRefObject } from 'react';

import Tag from '@amzn/meridian/tag';
import useIsT2Experience from 'src/hooks/useIsT2Experience';
import { pluralizeString } from 'src/utils/helpers';
import {
  ConflictColorCode,
  createDataCyForJob,
  getNumModifiedConflicts,
  hasJobBeenModified,
  hasJobBeenModifiedForAllConflicts,
  type TableDataRow,
} from 'src/utils/jmrUtils';
import { getConflictsType } from 'src/utils/placementSelectionUtils';

interface ConflictTagProps {
  jobMatch: TableDataRow;
  /** Popover anchor, omitted when the tag is rendered without a popover */
  buttonRef?: MutableRefObject<any>;
  /** A shift's tag is labelled with its shift code to distinguish it from the parent job's and its siblings' */
  shiftCode?: string;
  /** Multi-shift parents hold modifications on their shifts, so the tag cannot derive this from jobMatch */
  isModified?: boolean;
}

const ConflictTag = ({ jobMatch, buttonRef, shiftCode, isModified }: ConflictTagProps) => {
  const remainingConflicts = jobMatch?.conflicts.length - getNumModifiedConflicts(jobMatch.conflicts);
  const isUsingT2Experience = useIsT2Experience();

  const getT1ConflictsString = () => {
    if (hasJobBeenModifiedForAllConflicts(jobMatch.conflicts)) {
      return 'Modified';
    }

    return `${remainingConflicts} ${pluralizeString('Conflict', remainingConflicts)} ${hasJobBeenModified(jobMatch.conflicts) ? 'Remaining' : ''}`;
  };

  const getT2ConflictsString = () => {
    if (jobMatch.tierTwoPlusJobModified) {
      return jobMatch.modificationNotes?.trim() ? 'Modified' : 'Conflicts';
    }

    return '0 Conflicts';
  };

  const getConflictsString = () => {
    if (isModified) {
      return 'Modified';
    }

    return isUsingT2Experience ? getT2ConflictsString() : getT1ConflictsString();
  };

  const label = shiftCode ? `${jobMatch.name} ${shiftCode}` : jobMatch.name;
  const dataCy = shiftCode ? `${createDataCyForJob(jobMatch.name)}-${shiftCode}` : createDataCyForJob(jobMatch.name);

  return (
    // the tag class enlarges the popover hover target, so it only applies when a popover is anchored
    <div
      className={buttonRef ? 'conflictPopover__tag' : undefined}
      data-cy={`${dataCy}-conflictTag`}
      aria-label={`${label} Conflict Tag`}
    >
      <Tag ref={buttonRef} type={isModified ? ConflictColorCode.NO_CONFLICTS : getConflictsType(jobMatch)}>
        {getConflictsString()}
      </Tag>
    </div>
  );
};

export default ConflictTag;
